import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

// ── Types ─────────────────────────────────────────────────────────────────────
export interface Specialization {
  id: number;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  display_order?: number;
}

export interface Doctor {
  id: number;
  full_name: string;
  specializations: Specialization[];
  qualifications?: string;
  experience_years?: number;
  languages?: string;
  photo?: string | null;
  consult_fee: number;
  consult_fee_currency: string;
  avg_rating?: number;
  total_consults?: number;
  // detail-only
  bio?: string;
  registration_number?: string;
}

export interface Slot {
  start: string;   // ISO
  end: string;     // ISO
  available: boolean;
}

export type AppointmentStatus =
  | 'pending_payment' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
export type PaymentStatus = 'unpaid' | 'paid' | 'refunded' | 'waived';
export type RefundMode = 'source' | 'wallet';
export type RefundStatus = 'none' | 'pending' | 'completed' | 'failed';

export interface ConsultationSession {
  id: number;
  actual_start?: string | null;
  actual_end?: string | null;
  duration_seconds?: number;
  doctor_notes?: string;
  diagnosis?: string;
  advice?: string;
  prescription_notes?: string;
  follow_up_date?: string | null;
}

export interface Appointment {
  id: number;
  doctor: number;
  doctor_name: string;
  doctor_detail?: Doctor;
  dependant?: number | null;
  specialization?: number | null;
  specialization_name?: string | null;
  scheduled_start: string;
  scheduled_end: string;
  mode: 'video' | 'audio';
  status: AppointmentStatus;
  patient_notes?: string;
  fee_amount: number;
  currency: string;
  payment_status: PaymentStatus;
  room_id?: string;
  session?: ConsultationSession | null;
  cancelled_at?: string | null;
  cancelled_reason?: string;
  refund_status?: RefundStatus;
  refund_mode?: RefundMode | '';
  refund_amount?: number;
  refunded_at?: string | null;
  pending_reschedule?: Record<string, any> | null;
  created_at: string;
}

/** Reschedule result: either the updated appointment, or a top-up payment requirement. */
export interface RescheduleResult extends Appointment {
  reschedule_status?: 'moved' | 'refunded';
  refund_detail?: string;
  refund_amount?: number;
}
export interface RescheduleTopUp {
  status: 'top_up_required';
  top_up_amount: string;
  txn_id: string;
  payu_data: PayuData;
  detail?: string;
}

export interface DoctorAppointment {
  id: number;
  patient_id: number;
  patient_name: string;
  dependant?: number | null;
  dependant_name?: string | null;
  scheduled_start: string;
  scheduled_end: string;
  mode: 'video' | 'audio';
  status: AppointmentStatus;
  patient_notes?: string;
  room_id?: string;
  session?: ConsultationSession | null;
  created_at: string;
}

export interface SessionNotes {
  doctor_notes?: string;
  diagnosis?: string;
  advice?: string;
  follow_up_date?: string | null;
}

// ── Admin types ─────────────────────────────────────────────────────────────
export interface AdminSpecialization {
  id: number; name: string; slug: string; description?: string; icon?: string;
  is_active: boolean; display_order?: number;
}
export interface AdminSpecializationInput {
  name: string; description?: string; icon?: string; is_active?: boolean; display_order?: number;
}
export interface AdminAvailability {
  id?: number; weekday: number; start_time: string; end_time: string;
  slot_duration_minutes: number; is_active?: boolean;
}
export interface AdminDoctorDocument {
  id: number; doctor: number; title?: string; file: string; created_at?: string;
}
export interface AdminDoctor {
  id: number; user?: number | null; full_name: string;
  specializations: number[]; specialization_names: string[];
  qualifications?: string; registration_number?: string; experience_years?: number;
  languages?: string; bio?: string; photo?: string | null;
  consult_fee: number | string; consult_fee_currency: string; is_active: boolean;
  avg_rating?: number; total_consults?: number; availabilities: AdminAvailability[];
  documents?: AdminDoctorDocument[];
  has_login?: boolean; login_email?: string | null;
}
export interface AdminDoctorInput {
  full_name: string; specializations: number[]; qualifications?: string;
  registration_number?: string; experience_years?: number; languages?: string;
  bio?: string; consult_fee: number | string; consult_fee_currency?: string;
  is_active?: boolean; user?: number | null; availabilities?: AdminAvailability[];
}

export interface JoinToken {
  room_id: string;
  url: string;
  identity: string;
  role: 'patient' | 'doctor';
  token: string;
}

export interface PayuData {
  key: string; txnid: string; amount: string; productinfo: string;
  firstname: string; email: string; phone: string;
  surl: string; furl: string; hash: string;
  service_provider: string; payu_base_url: string;
}

export interface PaymentInit {
  appointment_id: number;
  txn_id: string;
  payu_data: PayuData;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const listOf = <T>(data: any): T[] =>
  Array.isArray(data) ? data : (data?.results ?? []);

// ── Service ───────────────────────────────────────────────────────────────────
const T = API_ENDPOINTS.teleconsultation;

export interface TeleconsultChoices {
  appointment_statuses: { value: AppointmentStatus; label: string }[];
  appointment_modes: { value: 'video' | 'audio'; label: string }[];
  payment_statuses: { value: PaymentStatus; label: string }[];
  refund_modes: { value: RefundMode; label: string }[];
  refund_statuses: { value: RefundStatus; label: string }[];
}

export const teleconsultationService = {
  async getChoices(): Promise<TeleconsultChoices> {
    const res = await axiosInstance.get(T.choices);
    return res.data;
  },

  async getSpecializations(): Promise<Specialization[]> {
    const res = await axiosInstance.get(T.specializations);
    return listOf<Specialization>(res.data);
  },

  async getDoctors(params?: { specialization?: number; search?: string }): Promise<Doctor[]> {
    const res = await axiosInstance.get(T.doctors, { params });
    return listOf<Doctor>(res.data);
  },

  async getDoctor(id: number): Promise<Doctor> {
    const res = await axiosInstance.get(T.doctorDetail(id));
    return res.data;
  },

  async getDoctorSlots(id: number, range?: { from?: string; to?: string }): Promise<Slot[]> {
    const res = await axiosInstance.get(T.doctorSlots(id), { params: range });
    return Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
  },

  async bookAppointment(payload: {
    doctor: number; scheduled_start: string; mode?: 'video' | 'audio';
    dependant?: number | null; patient_notes?: string; report_id?: number | null;
  }): Promise<Appointment> {
    const res = await axiosInstance.post(T.appointments, payload);
    return res.data;
  },

  async getAppointments(): Promise<Appointment[]> {
    const res = await axiosInstance.get(T.appointments);
    return listOf<Appointment>(res.data);
  },

  async getAppointment(id: number): Promise<Appointment> {
    const res = await axiosInstance.get(T.appointmentDetail(id));
    return res.data;
  },

  async cancelAppointment(
    id: number,
    opts?: { reason?: string; refund_mode?: RefundMode },
  ): Promise<Appointment & { refund_detail?: string }> {
    const res = await axiosInstance.patch(T.appointmentCancel(id), {
      reason: opts?.reason,
      refund_mode: opts?.refund_mode,
    });
    return res.data;
  },

  /**
   * Reschedule to a new slot (same or a different doctor).
   * Returns { topUp: RescheduleTopUp } when a top-up payment is required for a
   * pricier doctor — the caller must submit the PayU form. Otherwise returns
   * { appointment: RescheduleResult }.
   */
  async rescheduleAppointment(
    id: number,
    payload: { scheduled_start: string; doctor?: number; refund_mode?: RefundMode },
  ): Promise<{ appointment?: RescheduleResult; topUp?: RescheduleTopUp }> {
    try {
      const res = await axiosInstance.patch(T.appointmentReschedule(id), payload);
      return { appointment: res.data as RescheduleResult };
    } catch (e: any) {
      if (e?.response?.status === 402 && e.response.data?.status === 'top_up_required') {
        return { topUp: e.response.data as RescheduleTopUp };
      }
      throw e;
    }
  },

  async startAppointment(id: number): Promise<JoinToken> {
    const res = await axiosInstance.post(T.appointmentStart(id));
    return res.data;
  },

  async getJoinToken(id: number): Promise<JoinToken> {
    const res = await axiosInstance.get(T.appointmentJoinToken(id));
    return res.data;
  },

  async completeAppointment(id: number): Promise<Appointment> {
    const res = await axiosInstance.post(T.appointmentComplete(id));
    return res.data;
  },

  async createReview(payload: { appointment: number; rating: number; comment?: string }) {
    const res = await axiosInstance.post(T.reviews, payload);
    return res.data;
  },

  async initiatePayment(appointment_id: number): Promise<PaymentInit> {
    const res = await axiosInstance.post(T.initiatePayment, { appointment_id });
    return res.data;
  },

  /** Pay a pending appointment fully from the rupee wallet (balance must cover the fee). */
  async payWithWallet(appointment_id: number): Promise<{ detail: string; appointment: Appointment }> {
    const res = await axiosInstance.post(T.payWithWallet, { appointment_id });
    return res.data;
  },

  /**
   * Split payment: reserve the whole wallet balance and return PayU data for the
   * remaining card amount. The wallet is debited only once the card part clears.
   */
  async initiatePartialWalletPayment(appointment_id: number): Promise<{
    appointment_id: number; txn_id: string; wallet_applied: string; card_amount: string; payu_data: PayuData;
  }> {
    const res = await axiosInstance.post(T.payWithWalletPartial, { appointment_id });
    return res.data;
  },

  // ── Doctor console ──────────────────────────────────────────────────────────
  async getDoctorAppointments(status?: string): Promise<DoctorAppointment[]> {
    const res = await axiosInstance.get(T.doctorAppointments, { params: status ? { status } : undefined });
    return listOf<DoctorAppointment>(res.data);
  },

  async updateSession(id: number, data: SessionNotes): Promise<DoctorAppointment> {
    const res = await axiosInstance.patch(T.doctorSession(id), data);
    return res.data;
  },

  async createPrescription(id: number, payload: { prescription_text: string; record_name?: string }): Promise<{ detail: string; prescription_record_id: number }> {
    const res = await axiosInstance.post(T.doctorPrescription(id), payload);
    return res.data;
  },

  // ── Admin (super_admin) ─────────────────────────────────────────────────────
  async adminGetSpecializations(): Promise<AdminSpecialization[]> {
    const res = await axiosInstance.get(T.adminSpecializations);
    return listOf<AdminSpecialization>(res.data);
  },
  async adminSaveSpecialization(payload: AdminSpecializationInput, id?: number): Promise<AdminSpecialization> {
    const res = id
      ? await axiosInstance.patch(T.adminSpecializationDetail(id), payload)
      : await axiosInstance.post(T.adminSpecializations, payload);
    return res.data;
  },
  async adminDeleteSpecialization(id: number): Promise<void> {
    await axiosInstance.delete(T.adminSpecializationDetail(id));
  },
  async adminGetDoctors(): Promise<AdminDoctor[]> {
    const res = await axiosInstance.get(T.adminDoctors);
    return listOf<AdminDoctor>(res.data);
  },
  async adminSaveDoctor(payload: AdminDoctorInput, id?: number): Promise<AdminDoctor> {
    const res = id
      ? await axiosInstance.patch(T.adminDoctorDetail(id), payload)
      : await axiosInstance.post(T.adminDoctors, payload);
    return res.data;
  },
  async adminDeleteDoctor(id: number): Promise<void> {
    await axiosInstance.delete(T.adminDoctorDetail(id));
  },
  async adminUploadDoctorPhoto(id: number, file: File): Promise<AdminDoctor> {
    const fd = new FormData();
    fd.append('photo', file);
    const res = await axiosInstance.post(T.adminDoctorPhoto(id), fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  async adminUploadDoctorDocument(doctorId: number, file: File, title?: string): Promise<AdminDoctorDocument> {
    const fd = new FormData();
    fd.append('doctor', String(doctorId));
    fd.append('file', file);
    if (title) fd.append('title', title);
    const res = await axiosInstance.post(T.adminDoctorDocuments, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  async adminDeleteDoctorDocument(id: number): Promise<void> {
    await axiosInstance.delete(T.adminDoctorDocumentDetail(id));
  },
  async adminCreateDoctorLogin(id: number, email: string, mobile_number?: string): Promise<{ has_login: boolean; login_email: string; temp_password: string }> {
    const res = await axiosInstance.post(T.adminDoctorCreateLogin(id), { email, mobile_number });
    return res.data;
  },
  async adminResetDoctorPassword(id: number): Promise<{ login_email: string; temp_password: string }> {
    const res = await axiosInstance.post(T.adminDoctorResetPassword(id), {});
    return res.data;
  },
};

export default teleconsultationService;
